import { MapIcon } from 'features/mapping/mapping.types';
import { MapRef, PositionAnchor } from 'shared/map-exports';
import { LatLng } from 'shared/types';

export type IconFootprint = {
  centerOffsetPx: [number, number];
  radiusPx: number;
};

const getYOffset = (anchor: PositionAnchor, height: number): number => {
  if (anchor.startsWith('bottom')) {
    return -height / 2;
  }
  if (anchor.startsWith('top')) {
    return height / 2;
  }
  return 0;
};

export const iconFootprint = (
  icon: Pick<MapIcon, 'width' | 'height' | 'scale' | 'offset'>,
  anchor: PositionAnchor,
  componentScale = 1,
): IconFootprint => {
  const finalScale = componentScale * (icon.scale ?? 1);
  const w = icon.width * finalScale;
  const h = icon.height * finalScale;
  const ox = (icon.offset?.[0] ?? 0) * finalScale;
  const oy = (icon.offset?.[1] ?? 0) * finalScale;

  return {
    centerOffsetPx: [ox, oy + getYOffset(anchor, h)],
    radiusPx: Math.min(w, h) / 2,
  };
};

// Given a "from" location and a "to" location, shortens the line from the center of the icon
// to the approximate edge of the icon so that the line is not going through the icon
export const trimToIconEdge = (
  fromPx: { x: number; y: number },
  towardPx: { x: number; y: number },
  radius: number,
): { x: number; y: number } => {
  const dx = towardPx.x - fromPx.x;
  const dy = towardPx.y - fromPx.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= 0) return fromPx;

  const t = Math.min(radius / dist, 1);
  return { x: fromPx.x + dx * t, y: fromPx.y + dy * t };
};

// Projects two lat/lng points to screen pixels, trims each end to the icon edge, then unprojects
// back to lat/lng so the connector line stops at the icon boundary instead of running through it.
export const trimmedEndpointsLatLng = (
  map: MapRef,
  from: LatLng,
  to: LatLng,
  footprint: IconFootprint,
): { fromEdgeLatLng: LatLng; toEdgeLatLng: LatLng } => {
  const fromAnchor = map.project([from.lng, from.lat]);
  const toAnchor = map.project([to.lng, to.lat]);
  const [offsetX, offsetY] = footprint.centerOffsetPx;
  const fromCenter = { x: fromAnchor.x + offsetX, y: fromAnchor.y + offsetY };
  const toCenter = { x: toAnchor.x + offsetX, y: toAnchor.y + offsetY };
  const fromTrimmed = trimToIconEdge(fromCenter, toCenter, footprint.radiusPx);
  const toTrimmed = trimToIconEdge(toCenter, fromCenter, footprint.radiusPx);
  const fromEdgeLatLng = map.unproject([fromTrimmed.x, fromTrimmed.y]);
  const toEdgeLatLng = map.unproject([toTrimmed.x, toTrimmed.y]);

  return { fromEdgeLatLng, toEdgeLatLng };
};
